import i18n from '@i18next/i18n';
import { getTextInParagraphList } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/utils';
import {
    ChartTextProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/chart-shape-property';
import type { Nullable } from '../chart-shape-property.interface';
import type {
    IParagraph,
} from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import type {
    IChartManualLayout,
    IChartTitle,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import type { IChartTitleProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/title/interface';
import cloneDeep from 'lodash/cloneDeep';
import {
    STLayoutMode,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import { ChartActionType, IChartActionType } from '@src/slide/domain/ui-model/element-modeles/property/chart/constant';

const specialEditorOperationMapping: Record<IChartActionType, string> = {
    [ChartActionType.FONTFAMILY]: 'font-family',
    [ChartActionType.BOLD]: 'font-bold',
    [ChartActionType.ITALIC]: 'font-italic',
};

/**
 * 图表标题模块
 */
export default class ChartTitleProperty extends ChartTextProperty<IChartTitle> implements IChartTitleProperty {
    public removeTitle() {
        this.model.removeProps({ title: null });
    }

    public updateProps(props: Partial<IChartTitle>, removeProps?: Partial<Nullable<IChartTitle>>) {
        this.model.updateAndRemoveProps({ title: this.setTextRawMark(props) }, { title: removeProps });
    }

    /**
     * 更新标题自定义布局
     */
    public updateLayout(layout: IChartManualLayout) {
        const titleProps: IChartTitle = {
            layout,
        };

        if (layout.top !== undefined || layout.left !== undefined) {
            // 补充修改layout自定义位置的必要字段，否则office不生效
            if (!this.uiData.layout?.leftMode) {
                layout.leftMode = STLayoutMode.edge;
            }
            if (!this.uiData.layout?.topMode) {
                layout.topMode = STLayoutMode.edge;
            }
        }

        this.model.updateProps({ title: titleProps });
    }

    /**
     * 获取图表标题段落信息。
     * 会优先取富文本信息，否则取文本默认信息
     */
    public getParagraphList(opts?): IParagraph[] {
        const isSingleSeries = this.model.plotArea?.charts?.length === 1
            && this.model.plotArea?.charts[0]?.series?.length === 1;
        let title = isSingleSeries ? this.model.plotArea?.charts[0]?.series?.[0]?.name : '';
        title = title || i18n.t('图表标题');
        return super.getParagraphList(opts ?? {
            content: title,
            textAlign: 'center',
            level: 1,
        });
    }

    /**
     * 获取图表标题文本内容
     */
    public getChartTitleText(): string {
        const paragraphList = this.getParagraphList();
        return getTextInParagraphList(paragraphList);
    }

    public updateTitleStyle(action: IChartActionType, value) {
        // 有焦点执行选区更新
        if (this.isChartTitleInFocus()) {
            const result = this.transformChartActionToEditorOperation(action, value);
            if (result) {
                this.model.editorInstance?.operations(result.operation, result.value, {});
            }

            return;
        }

        // 执行整体model更新
        this.updateFontStyle(action, value);
    }

    private isChartTitleInFocus() {
        return !!this.model.editorInstance?.isEditing;
    }

    private transformChartActionToEditorOperation(
        chartAction: IChartActionType,
        value: any,
    ): { operation: string; value: any} | undefined {
        // 处理一下两边不一样的key，比如action里是fontfamily，operation里面是font-family
        const operation = specialEditorOperationMapping[chartAction] || chartAction;

        return {
            operation,
            // 处理方式
            value,
        };
    }


    private get model() {
        return this.context.getChartModel();
    }

    // 给pargraphList所有的属性都加上raw，防止在converter中被移除
    // 标题需要全量的paragraphList去做替换（当前阶段图表的文本的属性是增量更新，但是标题text更新涉及多个paragra和多个run的情况）
    // 所以paragraphList需要生成全量数据做替换
    private setTextRawMark(title: Partial<IChartTitle>): Partial<IChartTitle> {
        if (title.text?.paragraphList) {
            return {
                ...title,
                text: {
                    paragraphList: title.text.paragraphList.map((paragraph) => {
                        const paragraphItem = cloneDeep(paragraph);

                        [
                            'paragraphProps',
                            'endParaRPr',
                            'paragraphCharacter',
                        ].forEach((paragraphKey) => {
                            const value = paragraphItem[paragraphKey];

                            if (value !== undefined && value !== null) {
                                Object.keys(value).map((key) => {
                                    paragraphItem[paragraphKey][`raw-${key}`] = true;
                                });
                            }
                        });

                        if (paragraphItem.textRuns && paragraphItem.textRuns.length > 0) {
                            paragraphItem.textRuns.forEach((textRun) => {
                                const { styles } = textRun;

                                if (styles !== undefined && styles !== null) {
                                    Object.keys(styles).map((key) => {
                                        styles[`raw-${key}`] = true;
                                    });
                                }
                            });
                        }

                        return paragraphItem;
                    }),
                },
            };
        }

        return title;
    }
}

